import { useDeleteMappingData } from "../../../../api/drawingTool";
import { useDeleteMappingVersion } from "../../../../api/versioningService";
import { useInvalidateVersionCaches } from "../../../../components/drawing-tool/mapping-version/hooks/useInvalidateVersionCaches";
import { MappingSourceType } from "../../../../config/enums/MappingSourceType.enum";
import { successMessage } from "../../../../helpers/ErrorHandler";
import { DELETE_MAPPING_COPY } from "../constants/deleteMapping.constants";
import { usesMappingEndpoint } from "../helpers/deleteMapping.helpers";
import { DeleteMappingMode, DeleteMappingScope } from "../types/deleteMapping.types";

interface UseDeleteMappingSubmitArgs {
  drawingId: string | undefined;
  mappingSourceType: MappingSourceType;
  currentVersionId: string | undefined;
  mode: DeleteMappingMode;
  onDeleted: (scope: DeleteMappingScope) => void;
  onClose: () => void;
}

interface UseDeleteMappingSubmitResult {
  submit: (scope: DeleteMappingScope) => void;
  isDeleting: boolean;
}

/**
 * Routes to an endpoint and keeps the follow-up identical for both: close, tell the parent, and
 * drop the caches whose answers just changed.
 *
 * Not a plain scope -> endpoint map: in `NoVersionRow` mode the UI shows and forces the
 * current-version scope (there is no "all versions" left to offer), but there is no version id to
 * send a version-delete to, so that case is routed to the mapping-delete endpoint regardless of
 * scope -- see `usesMappingEndpoint`.
 */
export function useDeleteMappingSubmit({
  drawingId,
  mappingSourceType,
  currentVersionId,
  mode,
  onDeleted,
  onClose
}: UseDeleteMappingSubmitArgs): UseDeleteMappingSubmitResult {
  const { mutate: deleteVersion, isLoading: isDeletingVersion } = useDeleteMappingVersion();
  const { mutate: deleteMapping, isLoading: isDeletingMapping } = useDeleteMappingData(mappingSourceType);
  const invalidateVersionCaches = useInvalidateVersionCaches();

  const finish = (scope: DeleteMappingScope): void => {
    invalidateVersionCaches(drawingId);
    onClose();
    onDeleted(scope);
  };

  const submitCurrentVersion = (versionId: string): void => {
    deleteVersion(
      { versionId },
      {
        onSuccess: response => {
          // `remainingVersionCount === 0` is the unambiguous signal that this was the mapping's
          // only version, so removing it removed the mapping too -- not merely emptied a version.
          if (response.result.remainingVersionCount === 0) {
            successMessage(DELETE_MAPPING_COPY.mappingRemovedSuccess);
          } else {
            const label = response.result.activeVersion
              ? `Version ${response.result.deletedVersionNumber}`
              : "Version";
            successMessage(DELETE_MAPPING_COPY.currentVersionSuccess(label));
          }
          finish(DeleteMappingScope.CurrentVersion);
        }
        // No local onError handler: the axios interceptor already toasts the server's message
        // (or a network-failure fallback) for every failed request, so adding one here would
        // show the error twice.
      }
    );
  };

  const submitAllVersions = (): void => {
    if (!drawingId) {
      return;
    }

    // `useDeleteMappingData` toasts the server's success message itself -- do not add a second one.
    deleteMapping(drawingId, {
      onSuccess: () => {
        finish(DeleteMappingScope.AllVersions);
      }
    });
  };

  return {
    submit: (scope: DeleteMappingScope) => {
      // `NoVersionRow` forces the current-version scope in the UI (there is nothing else to
      // offer), but there is no version id to send it to -- route that case to the mapping-delete
      // endpoint instead, which is the only one that can act on an unversioned mapping. This is
      // the fix for the destructive button silently doing nothing when no version exists yet.
      if (scope === DeleteMappingScope.AllVersions || usesMappingEndpoint(mode)) {
        submitAllVersions();
        return;
      }

      if (!currentVersionId) {
        // Unreachable under normal use -- `resolveDeleteMode`/`resolveEffectiveScope` guarantee a
        // current-version id whenever this scope is reachable and `mode` isn't `NoVersionRow`.
        // Fall back to the endpoint that can always act, rather than doing nothing.
        submitAllVersions();
        return;
      }

      submitCurrentVersion(currentVersionId);
    },
    isDeleting: isDeletingVersion || isDeletingMapping
  };
}
